/**
 * Quick Order Builder — constructs prefilled PO data from inventory/stock items.
 *
 * Used by Operations page, Dashboard alerts, and Inventory page
 * to create POs from low stock / reorder items.
 */

/**
 * Build prefill data for a single item quick order.
 * Returns object suitable for passing to Orders page via navigate state.
 */
export function buildQuickOrderPrefill(item, locationId) {
  const qty = computeReorderQty(item);
  return {
    location_id: locationId || item.location_id,
    supplier_id: item.default_supplier_id || item.supplier_id || null,
    supplier_name: item.supplier_name || null,
    items: [{
      product_id: item.product_id || item.id,
      product_name: item.product_name || item.name,
      qty_ordered: qty,
      unit_cost: Number(item.unit_cost) || 0,
    }],
    notes: 'Quick order from low stock alert',
  };
}

/**
 * Build prefill data for multiple items grouped by supplier.
 * Returns array of prefill objects (one per supplier).
 */
export function buildBulkOrderPrefills(items, locationId) {
  const groups = {};
  for (const item of items) {
    const supplierId = item.default_supplier_id || item.supplier_id;
    if (!supplierId) continue;
    if (!groups[supplierId]) {
      groups[supplierId] = {
        location_id: locationId || item.location_id,
        supplier_id: supplierId,
        supplier_name: item.supplier_name,
        items: [],
        notes: 'Bulk reorder from low stock alerts',
      };
    }
    groups[supplierId].items.push({
      product_id: item.product_id || item.id,
      product_name: item.product_name || item.name,
      qty_ordered: computeReorderQty(item),
      unit_cost: Number(item.unit_cost) || 0,
    });
  }
  return Object.values(groups);
}

/**
 * Compute suggested reorder quantity for an item.
 * Uses reorder_qty if set, otherwise calculates from par levels.
 */
export function computeReorderQty(item) {
  const reorderQty = Number(item.reorder_qty);
  if (reorderQty > 0) return reorderQty;

  const parMax = Number(item.par_max);
  const currentStock = Number(item.current_stock) || 0;
  if (parMax > 0) return Math.max(1, Math.ceil(parMax - currentStock));

  const parMin = Number(item.par_min);
  if (parMin > 0) return Math.max(1, Math.ceil(parMin * 2 - currentStock));

  // Fallback: order at least 1
  return 1;
}
